import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import useViewportMode from '../../hooks/useViewportMode';
import {
  cloneOngoingAssessmentReplay,
  OngoingAssessmentReplay,
  ongoingAssessmentReplayFingerprint,
} from '../../services/OngoingAssessmentReplayService';

export const serializeOngoingAssessmentDraft = (replay: OngoingAssessmentReplay): string => JSON.stringify({
  Criteria: replay.Criteria.map(({ Id, Rating, Note }) => ({ Id, Rating, Note })),
  SupervisorNote: replay.SupervisorNote,
});

const OngoingAssessmentReplayPresentation = ({ replay }: { replay: OngoingAssessmentReplay }) => {
  const viewport = useViewportMode();
  const [draft, setDraft] = useState(() => cloneOngoingAssessmentReplay(replay));
  const [section, setSection] = useState<'assessment' | 'events' | 'history'>('assessment');
  const [group, setGroup] = useState('All');
  const [checklistOpen, setChecklistOpen] = useState(false);
  const baseline = useMemo(() => serializeOngoingAssessmentDraft(replay), [replay]);
  const current = serializeOngoingAssessmentDraft(draft);
  const changed = current !== baseline;

  const updateCriterion = (id: string, field: 'Rating' | 'Note', value: string) => setDraft(previous => ({
    ...previous,
    Criteria: previous.Criteria.map(criterion => criterion.Id === id ? { ...criterion, [field]: value } : criterion),
  }));

  const groups = ['All', ...Array.from(new Set(draft.Criteria.map(criterion => criterion.Group)))];

  return <Container maxWidth="xl" disableGutters={viewport.isPhone} sx={{ py: { xs: 0, sm: 2 } }}>
    <Paper
      square={viewport.isPhone}
      data-testid="ongoing-assessment-replay"
      data-layout={viewport.isPhone ? 'phone-cards' : viewport.isTablet ? 'tablet-cards' : 'desktop-grid'}
      data-fixture-fingerprint={ongoingAssessmentReplayFingerprint(replay)}
      data-draft-serialization={current}
      sx={{ minHeight: { xs: '100dvh', sm: 'calc(100dvh - 112px)' }, overflow: 'hidden' }}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
          <Box>
            <Typography variant="overline" color="primary.main">SYNTHETIC DEMONSTRATION · READ ONLY</Typography>
            <Typography variant="h4" fontWeight={900}>Ongoing assessment draft</Typography>
            <Typography color="text.secondary">{draft.TalentName} · {draft.PositionName}</Typography>
          </Box>
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <Chip label={changed ? 'In-memory edits' : 'Canonical baseline'} color={changed ? 'warning' : 'success'} />
          </Stack>
        </Stack>
        <Alert severity="info" sx={{ mt: 2 }}>
          Ratings and notes are synthetic training data. No assessment, notification, integration, or organization record can change.
        </Alert>
      </Box>
      <Divider />
      <Tabs value={section} onChange={(_, value) => setSection(value)} aria-label="Assessment workspace sections" sx={{ px: 2 }}>
        <Tab value="assessment" label="Assessment" />
        <Tab value="events" label="Events" />
        <Tab value="history" label="History & goals" />
      </Tabs>
      <Divider />
      <Box sx={{ p: { xs: 2, md: 3 }, overflowY: 'auto', maxHeight: { sm: 'calc(100dvh - 260px)' } }}>
        {section === 'events' && <Stack spacing={2}>
          <Typography variant="h6" fontWeight={800}>Review-period events</Typography>
          {draft.Events.map(event => <Paper variant="outlined" key={event.Title} sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">{event.Date}</Typography><Typography fontWeight={800}>{event.Title}</Typography><Typography>{event.Description}</Typography></Paper>)}
          <Button variant="outlined" onClick={() => setChecklistOpen(true)}>Open evidence checklist</Button>
        </Stack>}
        {section === 'history' && <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}><Typography variant="overline" color="text.secondary">Prior assessment · {draft.PriorAssessment.Date}</Typography><Typography>{draft.PriorAssessment.Summary}</Typography></Paper>
          <Paper variant="outlined" sx={{ p: 2 }}><Stack direction="row" justifyContent="space-between"><Typography fontWeight={800}>{draft.ActiveGoal.Title}</Typography><Chip label={draft.ActiveGoal.Status} color="success" /></Stack><Typography sx={{ mt: 1 }}>{draft.ActiveGoal.Narrative}</Typography></Paper>
        </Stack>}
        {section === 'assessment' && <>
        <Tabs value={group} onChange={(_, value) => setGroup(value)} variant="scrollable" aria-label="Assessment categories" sx={{ mb: 2 }}>
          {groups.map(value => <Tab key={value} value={value} label={value} />)}
        </Tabs>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(300px, .38fr) minmax(0, 1fr)' }, gap: 2.5, alignItems: 'start' }}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={800}>Review-period evidence</Typography>
              <Stack divider={<Divider flexItem />} spacing={1.5} sx={{ mt: 1.5 }}>
                {draft.Events.map(event => <Box key={event.Title}>
                  <Typography variant="caption" color="text.secondary">{event.Date}</Typography>
                  <Typography fontWeight={750}>{event.Title}</Typography>
                  <Typography variant="body2" color="text.secondary">{event.Description}</Typography>
                </Box>)}
              </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="overline" color="text.secondary">Prior assessment · {draft.PriorAssessment.Date}</Typography>
              <Typography variant="body2">{draft.PriorAssessment.Summary}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" gap={1} justifyContent="space-between"><Typography fontWeight={800}>{draft.ActiveGoal.Title}</Typography><Chip size="small" label={draft.ActiveGoal.Status} color="success" /></Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{draft.ActiveGoal.Narrative}</Typography>
            </Paper>
          </Stack>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={800}>Editable assessment draft</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
              {draft.Criteria.filter(criterion => group === 'All' || criterion.Group === group).map(criterion => <Paper key={criterion.Id} variant="outlined" component="article" sx={{ p: 2, minWidth: 0 }}>
                <Stack spacing={1.25}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{criterion.Group} · {criterion.Kind}</Typography>
                    <Typography fontWeight={800} sx={{ overflowWrap: 'anywhere' }}>{criterion.Name}</Typography>
                  </Box>
                  <TextField
                    select
                    size="small"
                    label={`Rating for ${criterion.Name}`}
                    value={criterion.Rating}
                    onChange={event => updateCriterion(criterion.Id, 'Rating', event.target.value)}>
                    {criterion.RatingOptions.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                  </TextField>
                  <TextField
                    size="small"
                    multiline
                    minRows={2}
                    label={`Evidence note for ${criterion.Name}`}
                    value={criterion.Note}
                    onChange={event => updateCriterion(criterion.Id, 'Note', event.target.value)} />
                </Stack>
              </Paper>)}
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Supervisor note"
              value={draft.SupervisorNote}
              onChange={event => setDraft(previous => ({ ...previous, SupervisorNote: event.target.value }))} />
          </Stack>
        </Box>
        </>}
      </Box>
      <Dialog open={checklistOpen} onClose={() => setChecklistOpen(false)} aria-labelledby="evidence-checklist-title">
        <DialogTitle id="evidence-checklist-title">Evidence checklist</DialogTitle>
        <DialogContent dividers><Stack spacing={1}>{['Specific date and observable outcome', 'Connection to a role criterion', 'Balanced strengths and development context', 'Supervisor verification before publication'].map(item => <Typography key={item}>✓ {item}</Typography>)}</Stack></DialogContent>
        <DialogActions><Button onClick={() => setChecklistOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </Paper>
  </Container>;
};

export default OngoingAssessmentReplayPresentation;
